import { CRM_TAXONOMY_AUTOMATION_CATEGORIES } from "./crmFilterTaxonomy.automation";
import { CRM_TAXONOMY_BILLING_CATEGORIES } from "./crmFilterTaxonomy.billing";
import { CRM_TAXONOMY_PORTAL_CATEGORIES } from "./crmFilterTaxonomy.portal";
import {
  CRM_FILTER_TAXONOMY_OPERATORS,
  type CrmFilterTaxonomyOperator,
  type CrmFilterTaxonomyOperatorId,
} from "./crmFilterTaxonomy.operators";
import { CRM_FILTER_TAXONOMY_DATA_CHECKS } from "./crmFilterTaxonomy.pending";

const CRM_FILTER_TAXONOMY_CATALOG = [
  ...CRM_TAXONOMY_AUTOMATION_CATEGORIES,
  ...CRM_TAXONOMY_BILLING_CATEGORIES,
  ...CRM_TAXONOMY_PORTAL_CATEGORIES,
] as const;

export { CRM_FILTER_TAXONOMY_OPERATORS, CRM_FILTER_TAXONOMY_DATA_CHECKS };
export type { CrmFilterTaxonomyOperator, CrmFilterTaxonomyOperatorId };
export * from "./crmFilterTaxonomy.compatibility";

export const CRM_FILTER_TAXONOMY_SOURCE = {
  version: "1.5",
  date: "2026-09-18",
  document:
    "docs/admin/evidence/source-artifacts/kate-admin-20260921/drive/18-Filters_taxonomy_canonical.docx",
  sha256: "e4c5d8a1fd9c709de0423bc4056b37c254b9ccba90d2e2958bba94b28fdb8230",
  declaredCategories: 23,
  declaredFields: 98,
  declaredPendingFields: 37,
  // The document groups three UTM identities in its final data-check row.
  dataCheckRows: 37,
  pendingFieldIdentities: 39,
} as const;

type CatalogCategory = (typeof CRM_FILTER_TAXONOMY_CATALOG)[number];
export type CrmFilterTaxonomyCategoryId = CatalogCategory["id"];
export type CrmFilterTaxonomyFieldId =
  | CatalogCategory["fields"][number][0]
  // Approved later Contact payer contract. This does not rewrite the v1.5
  // source inventory or make the field available to any other consumer.
  | "organisation_paid_by"
  // Approved later Contact sign-up intent (Kate, 1 October 2026; closeout
  // crm-closeout-20261001.md). Likewise outside the v1.5 inventory.
  | "started_signup";
export type CrmFilterTaxonomyValueSource =
  | "static-list"
  | "live-list"
  | "captured-value"
  | "unconfigured"
  | "number"
  | "relative-window"
  | "none";

export interface CrmFilterTaxonomyField {
  readonly id: CrmFilterTaxonomyFieldId;
  readonly label: string;
  readonly categoryId: CrmFilterTaxonomyCategoryId;
  readonly categoryNumber: number;
  /** One-based inventory row; zero only for an explicit later UI contract. */
  readonly sourceRow: number;
  readonly operators: readonly CrmFilterTaxonomyOperator[];
  /** Exact source cells, including placeholders; not hydrated selectable options. */
  readonly values: readonly string[];
  readonly valueSource: CrmFilterTaxonomyValueSource;
  readonly pendingDataCheck: boolean;
  readonly dataCheckRow: number | null;
  readonly sourceBlock: "carousel-state-unavailable" | null;
}

const valueSource = (
  values: readonly string[],
): CrmFilterTaxonomyValueSource => {
  const first = values[0] ?? "";
  if (first === "—") return "none";
  if (first.startsWith("Custom ")) return "number";
  if (first.startsWith("Last ")) return "relative-window";
  if (first.startsWith("Values to be set")) return "unconfigured";
  if (first === "Captured value") return "captured-value";
  if (
    first.startsWith("Live ") ||
    first === "Step name from the named workflow"
  )
    return "live-list";
  return "static-list";
};

/** Source inventory, NOT an availability or entity-authorisation registry. */
export const CRM_FILTER_TAXONOMY_FIELDS: readonly CrmFilterTaxonomyField[] =
  CRM_FILTER_TAXONOMY_CATALOG.flatMap((category) =>
    category.fields.map(
      ([id, label, labels, values], index): CrmFilterTaxonomyField => {
        const dataCheck = CRM_FILTER_TAXONOMY_DATA_CHECKS.find((row) =>
          (row.fieldIds as readonly string[]).includes(id),
        );
        return {
          id,
          label,
          categoryId: category.id,
          categoryNumber: category.number,
          sourceRow: index + 1,
          operators: labels.map((label) => {
            const operator = CRM_FILTER_TAXONOMY_OPERATORS.find(
              (entry) => entry.label === label,
            );
            if (!operator)
              throw new Error("Unknown taxonomy operator in source catalog");
            return operator;
          }),
          values,
          valueSource: valueSource(values),
          pendingDataCheck: dataCheck !== undefined,
          dataCheckRow: dataCheck?.row ?? null,
          sourceBlock:
            id === "feature_carousel" || id === "carousels_complete"
              ? "carousel-state-unavailable"
              : null,
        };
      },
    ),
  );

export const CRM_FILTER_TAXONOMY_CATEGORIES = CRM_FILTER_TAXONOMY_CATALOG.map(
  ({ id, number, label }) => ({
    id,
    number,
    label,
    fields: CRM_FILTER_TAXONOMY_FIELDS.filter(
      (field) => field.categoryId === id,
    ),
  }),
);

export const getCrmFilterTaxonomyField = (
  id: string,
): CrmFilterTaxonomyField | undefined =>
  CRM_FILTER_TAXONOMY_FIELDS.find((field) => field.id === id);

/**
 * Fail-closed inventory selection; not an entity capability or permission gate.
 * Callers must separately prove entity/operator/data support and authorisation.
 * Even an explicit caller list cannot enable a source-pending or blocked field.
 * No data availability is inferred by this inventory helper.
 */
export const getCrmFilterTaxonomyCandidates = (
  implementedFieldIds: readonly string[] = [],
) =>
  CRM_FILTER_TAXONOMY_FIELDS.filter(
    (field) =>
      implementedFieldIds.includes(field.id) &&
      !field.pendingDataCheck &&
      !field.sourceBlock,
  );

/**
 * Operator operand and optional named subject are separate: e.g. Email sent /
 * Has happened has no operand but may name an email on that same row.
 * This describes the source, not an executable saved-clause format.
 */
export const getCrmFilterTaxonomyControl = (
  fieldId: string,
  operatorId: string,
) => {
  const field = getCrmFilterTaxonomyField(fieldId);
  const operator = field?.operators.find((entry) => entry.id === operatorId);
  if (!field || !operator) return undefined;
  const hasList = ["static-list", "live-list", "captured-value"].includes(
    field.valueSource,
  );
  let control: CrmFilterTaxonomyOperator["control"] = operator.control;
  if (control === "number-and-list" && !hasList) control = "number";
  if (control === "single-select" && field.valueSource === "number")
    control = "number";
  // No selectable value can be invented for the source's em-dash cells.
  if (control === "single-select" && field.valueSource === "none")
    control = "none";
  const namedSubject = [
    "feature_carousel",
    "feature_used",
    "form_submitted",
    "budget_line",
    "email_sent",
    "email_opened",
    "email_clicked",
    "email_bounced",
    "email_failed",
  ].includes(field.id);
  return {
    control,
    minimumSelections: control === "multi-select" ? 1 : 0,
    numberWithValueList: control === "number-and-list",
    namedSubject: namedSubject ? ("optional-same-row" as const) : null,
    requiresWorkflow: field.id === "workflow_step",
    // Has rows bind their window to this condition, never to a second clause.
    eventWindow: operator.label.startsWith("Has ")
      ? ("same-row" as const)
      : null,
    valueSource: field.valueSource,
  };
};
